import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, ArrowRight, Calendar, TrendingUp, MapPin, Loader2, Plus, X, ChevronDown, Clock, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import { OrganizationDashboard as OrgDashboardData, OrganizationMembership, PracticeStats } from '../types/organization';

interface CreatePracticeForm {
  name: string;
  phone: string;
  email: string;
  address_line1: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  timezone: string;
}

const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: 'UTC-8' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: 'UTC-7' },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: 'UTC-6' },
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: 'UTC-5' },
  { value: 'America/Anchorage', label: 'Alaska Time', offset: 'UTC-9' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time', offset: 'UTC-10' },
];

const initialPracticeForm: CreatePracticeForm = {
  name: '',
  phone: '',
  email: '',
  address_line1: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'US',
  timezone: 'America/Los_Angeles',
};

const OrganizationDashboard: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<OrganizationMembership[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<OrgDashboardData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [switching, setSwitching] = useState<string | null>(null);
  
  // Create practice modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreatePracticeForm>(initialPracticeForm);
  const [creating, setCreating] = useState(false);
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const timezoneButtonRef = useRef<HTMLButtonElement>(null);
  
  const [showSeatLimitWarning, setShowSeatLimitWarning] = useState(false);
  
  // Close timezone dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (timezoneButtonRef.current && !timezoneButtonRef.current.contains(event.target as Node)) {
        const dropdown = document.getElementById('timezone-dropdown-portal');
        if (dropdown && !dropdown.contains(event.target as Node)) {
          setShowTimezoneDropdown(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Update dropdown position when opening
  useEffect(() => {
    if (showTimezoneDropdown && timezoneButtonRef.current) {
      const rect = timezoneButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [showTimezoneDropdown]);
  
  // Fetch user's organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/v1/organizations/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to load organizations');
        }
        
        const data: OrganizationMembership[] = await response.json();
        setMemberships(data);
        
        // Auto-select first org if available
        if (data.length > 0 && !selectedOrgId) {
          setSelectedOrgId(data[0].organization_id);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrganizations();
  }, []);
  
  // Fetch dashboard data when org is selected
  useEffect(() => {
    if (!selectedOrgId) return;
    
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_BASE_URL}/api/v1/organizations/${selectedOrgId}/dashboard`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to load dashboard');
        }
        
        const data: OrgDashboardData = await response.json();
        setDashboardData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboard();
  }, [selectedOrgId]);
  
  // Handle practice switch
  const handleSwitchPractice = async (practiceId: string, practiceName: string) => {
    if (!selectedOrgId) return;
    
    setSwitching(practiceId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/organizations/${selectedOrgId}/switch-practice`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ practice_id: practiceId }),
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to switch practice');
      }
      
      // Refresh user context and navigate
      await refreshUser();
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to switch practice');
    } finally {
      setSwitching(null);
    }
  };
  
  // Filter practices by search term
  const filteredPractices = dashboardData?.practices.filter(p => 
    p.practice_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.state?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  // Find current org
  const currentOrg = memberships.find(m => m.organization_id === selectedOrgId);
  const isOrgAdmin = currentOrg?.role === 'ORG_ADMIN';
  
  const currentPracticeCount = currentOrg?.practice_count || 0;
  const currentSeatCount = currentOrg?.seat_count || 1;
  const atSeatLimit = currentPracticeCount >= currentSeatCount;

  const handleAddPracticeClick = () => {
    if (atSeatLimit) {
      setShowSeatLimitWarning(true);
    } else {
      setShowCreateModal(true);
    }
  };
  
  // Handle creating a new practice
  const handleCreatePractice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrgId || !createForm.name.trim()) return;
    
    setCreating(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/organizations/${selectedOrgId}/practices`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(createForm),
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create practice');
      }
      
      // Success - close modal and refresh dashboard
      setShowCreateModal(false);
      setCreateForm(initialPracticeForm);
      
      // Re-fetch dashboard data
      const dashboardResponse = await fetch(
        `${API_BASE_URL}/api/v1/organizations/${selectedOrgId}/dashboard`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      if (dashboardResponse.ok) {
        const data: OrgDashboardData = await dashboardResponse.json();
        setDashboardData(data);
      }
      
      // Also refresh memberships to update practice count
      const orgsResponse = await fetch(`${API_BASE_URL}/api/v1/organizations/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (orgsResponse.ok) {
        const orgsData: OrganizationMembership[] = await orgsResponse.json();
        setMemberships(orgsData);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create practice');
    } finally {
      setCreating(false);
    }
  };
  
  if (loading && memberships.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }
  
  if (memberships.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Organizations</h2>
          <p className="text-gray-600">You are not a member of any organization.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-emerald-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {currentOrg?.organization_name || 'Organization Dashboard'}
                </h1>
                <p className="text-sm text-gray-500">
                  {currentOrg?.practice_count} practice{currentOrg?.practice_count !== 1 ? 's' : ''} in this organization
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Create Practice button for ORG_ADMIN */}
              {isOrgAdmin && (
                <button
                  onClick={handleAddPracticeClick}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                >
                  <Plus className="w-5 h-5" />
                  Add Practice
                </button>
              )}
              
              {/* Org selector if multiple orgs */}
              {memberships.length > 1 && (
                <select
                  value={selectedOrgId || ''}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {memberships.map(m => (
                    <option key={m.organization_id} value={m.organization_id}>
                      {m.organization_name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Summary */}
      {dashboardData && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Today's Appointments</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardData.totals.today}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">This Week</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardData.totals.this_week}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">This Month</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardData.totals.this_month}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search practices by name, city, or state..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
          
          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
              <button 
                onClick={() => setError(null)}
                className="ml-4 text-red-500 hover:text-red-700"
              >
                Dismiss
              </button>
            </div>
          )}
          
          {/* Practices Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Practices</h2>
              <span className="text-sm text-gray-500">
                {filteredPractices.length} of {dashboardData.practices.length} practices
              </span>
            </div>
            
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Practice
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Today
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        This Week
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        This Month
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredPractices.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          {searchTerm ? 'No practices match your search' : 'No practices found'}
                        </td>
                      </tr>
                    ) : (
                      filteredPractices.map((practice) => (
                        <tr 
                          key={practice.practice_id}
                          className={`hover:bg-gray-50 ${
                            user?.practice_id === practice.practice_id ? 'bg-emerald-50' : ''
                          }`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <Building2 className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900">{practice.practice_name}</p>
                                {user?.practice_id === practice.practice_id && (
                                  <span className="text-xs text-emerald-600 font-medium">Current</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {practice.city || practice.state ? (
                              <div className="flex items-center gap-1 text-gray-600">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <span>{[practice.city, practice.state].filter(Boolean).join(', ')}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                              practice.appointments_today > 0 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {practice.appointments_today}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-gray-900">{practice.appointments_this_week}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-gray-900">{practice.appointments_this_month}</span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {isOrgAdmin && user?.practice_id !== practice.practice_id && (
                              <button
                                onClick={() => handleSwitchPractice(practice.practice_id, practice.practice_name)}
                                disabled={switching !== null}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {switching === practice.practice_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <span>Switch</span>
                                    <ArrowRight className="w-4 h-4" />
                                  </>
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Seat Limit Reached Modal */}
      {showSeatLimitWarning && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-xl font-semibold text-gray-900">Seat Limit Reached</h2>
                <button
                  onClick={() => setShowSeatLimitWarning(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-6 pb-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Current seats</span>
                  <span className="font-semibold text-gray-900">{currentSeatCount}</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2">
                  <span className="text-gray-500">Active practices</span>
                  <span className="font-semibold text-gray-900">{currentPracticeCount}</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-5">
                To add another practice, contact our sales team to increase your seat allocation.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSeatLimitWarning(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-900 rounded-xl hover:bg-gray-200 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <a
                  href="mailto:hi@helppet.ai?subject=Increase%20seat%20allocation"
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-medium transition-colors text-center"
                >
                  Contact Sales
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Practice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Add New Practice</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreatePractice} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Practice Name *
                </label>
                <input
                  type="text"
                  required
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Acme Veterinary Clinic"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="clinic@example.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={createForm.address_line1}
                  onChange={(e) => setCreateForm({ ...createForm, address_line1: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="123 Main Street"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={createForm.city}
                    onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="San Francisco"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={createForm.state}
                    onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="CA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP
                  </label>
                  <input
                    type="text"
                    value={createForm.postal_code}
                    onChange={(e) => setCreateForm({ ...createForm, postal_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="94102"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <button
                  ref={timezoneButtonRef}
                  type="button"
                  onClick={() => setShowTimezoneDropdown(!showTimezoneDropdown)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:border-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900">
                      {TIMEZONE_OPTIONS.find(tz => tz.value === createForm.timezone)?.label || 'Select timezone'}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTimezoneDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Portal-based dropdown to escape modal overflow */}
                {showTimezoneDropdown && ReactDOM.createPortal(
                  <div 
                    id="timezone-dropdown-portal"
                    className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-60 overflow-auto"
                    style={{
                      top: dropdownPosition.top,
                      left: dropdownPosition.left,
                      width: dropdownPosition.width,
                      zIndex: 9999,
                    }}
                  >
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <button
                        key={tz.value}
                        type="button"
                        onClick={() => {
                          setCreateForm({ ...createForm, timezone: tz.value });
                          setShowTimezoneDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left flex items-center justify-between hover:bg-gray-50 ${
                          createForm.timezone === tz.value ? 'bg-emerald-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Clock className={`w-4 h-4 ${createForm.timezone === tz.value ? 'text-emerald-600' : 'text-gray-400'}`} />
                          <div>
                            <p className={`font-medium ${createForm.timezone === tz.value ? 'text-emerald-700' : 'text-gray-900'}`}>
                              {tz.label}
                            </p>
                            <p className="text-xs text-gray-500">{tz.offset}</p>
                          </div>
                        </div>
                        {createForm.timezone === tz.value && (
                          <Check className="w-4 h-4 text-emerald-600" />
                        )}
                      </button>
                    ))}
                  </div>,
                  document.body
                )}
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !createForm.name.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Practice
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationDashboard;
